import type React from 'react';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { MessageScreen, messageScreenActionClassName } from '../components';
import { carriedSearch, useAuthNavigation } from '../navigation';
import { applyPreviewParam, hasAuthPreview, PREVIEW_PARAM, type PreviewStorage, windowStorage } from './auth-preview';

export interface AuthPreviewGateProps {
  /** Whether the gate is on: AUTH_PREVIEW_REQUIRED, from the build. */
  required: boolean;
  /** Where the unlock flag lives. Defaults to window.localStorage. */
  storage?: () => PreviewStorage | undefined;
  children: React.ReactNode;
}

/** What a locked /sign-in or /register shows: nothing from Firebase is loaded. */
export const ComingSoon: React.FC<{ returnUrl: string }> = ({ returnUrl }) => (
  <MessageScreen
    title="Coming soon"
    mood="excited"
    action={
      <a className={messageScreenActionClassName} href={returnUrl}>
        Back to the game
      </a>
    }
  >
    <p>Words Happen accounts are almost ready. Until then, you can keep playing without one.</p>
  </MessageScreen>
);

/**
 * The pre-launch gate on sign-in and registration (removed at go-live, ENG-881). With `required` on, the children
 * show only once ?preview=on has saved the flag in storage; otherwise "Coming soon". The real gate against bots is
 * Identity Platform refusing browser sign-ups in production; this only hides the pages. ?preview=on and
 * ?preview=off are removed from the address once applied, keeping return_to and provider.
 */
export const AuthPreviewGate: React.FC<AuthPreviewGateProps> = ({ required, storage = windowStorage, children }) => {
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { returnUrl } = useAuthNavigation();
  const preview = params.get(PREVIEW_PARAM);

  // biome-ignore lint/correctness/useExhaustiveDependencies: storage is read again only when the parameter changes
  const unlocked = useMemo(() => {
    const store = storage();
    applyPreviewParam(preview, store);
    return hasAuthPreview(store);
  }, [preview]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once per parameter, not on every params identity
  useEffect(() => {
    if (preview !== null) {
      navigate({ search: carriedSearch(params), hash }, { replace: true });
    }
  }, [preview]);

  return !required || unlocked ? children : <ComingSoon returnUrl={returnUrl} />;
};
